import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pharmacyAPI, pharmacyWardAPI, wardInventoryAPI } from '../../utils/api';
import PharmacyWardReport from './PharmacyWardReport';
import { toast } from 'react-toastify';
import useDebounce from '../../utils/useDebounce';

const CATEGORIES = ['Antibiotic', 'Analgesic', 'Antihypertensive', 'Antidiabetic', 'Antacid', 'Antihistamine', 'Cardiac', 'Respiratory', 'Neurological', 'Vitamin', 'IV Fluid', 'Injection', 'Other'];
const FORMS = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Infusion', 'Cream', 'Drops', 'Inhaler', 'Suppository', 'Sachet', 'Spray', 'Nebulizer', 'Ampoule'];

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, background: 'white' };

function expiryStatus(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    const exp = new Date(dateStr);
    const diff = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { label: 'EXPIRED', color: '#dc2626', bg: '#fef2f2' };
    if (diff <= 90) return { label: `Exp in ${diff}d`, color: '#d97706', bg: '#fffbeb' };
    return { label: new Date(dateStr).toLocaleDateString('en-PK', { month: 'short', year: 'numeric' }), color: '#16a34a', bg: '#f0fdf4' };
}

export default function PharmacyDashboard() {
    const navigate = useNavigate();
    const [drugs, setDrugs] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('');
    const [invSearch, setInvSearch] = useState('');
    const [tab, setTab] = useState('drugs');
    const [showAddDrug, setShowAddDrug] = useState(false);
    const [showAddStock, setShowAddStock] = useState(null);
    const [loading, setLoading] = useState(false);
    const [invLoading, setInvLoading] = useState(false);

    // Ward requests state
    const [wardRequests, setWardRequests] = useState([]);
    const [wardReqLoading, setWardReqLoading] = useState(false);
    const [pendingCount, setPendingCount] = useState({ ward_requests: 0, consumables: 0, returns: 0 });
    const [issueModal, setIssueModal] = useState(null);
    const [issueQty, setIssueQty] = useState('');
    const [consumables, setConsumables] = useState([]);
    const [wardReturns, setWardReturns] = useState([]);
    const [deptRequests, setDeptRequests] = useState([]);
    const [deptReqLoading, setDeptReqLoading] = useState(false);
    const [deptIssueModal, setDeptIssueModal] = useState(null);
    const [deptIssueQty, setDeptIssueQty] = useState('');

    const [drugForm, setDrugForm] = useState({ name: '', generic_name: '', category: '', form: 'Tablet', strength: '', unit: 'Tablet', reorder_level: 10 });
    const [stockForm, setStockForm] = useState({ quantity: '', batch_number: '', expiry_date: '', supplier: '', purchase_price: '', sale_price: '' });

    const debouncedSearch = useDebounce(search, 400);
    const debouncedInvSearch = useDebounce(invSearch, 400);

    const loadDrugs = useCallback(async () => {
        try {
            const res = await pharmacyAPI.getDrugs({ q: debouncedSearch, category: catFilter });
            setDrugs(res.data);
        } catch { toast.error('Failed to load drugs'); }
    }, [debouncedSearch, catFilter]);

    const loadLowStock = async () => {
        try {
            const res = await pharmacyAPI.getLowStock();
            setLowStock(res.data);
        } catch {}
    };

    const loadInventory = useCallback(async () => {
        setInvLoading(true);
        try {
            const res = await pharmacyAPI.getInventory({ q: debouncedInvSearch });
            setInventory(res.data);
        } catch { toast.error('Failed to load inventory'); }
        finally { setInvLoading(false); }
    }, [debouncedInvSearch]);

    const loadDeptRequests = useCallback(async () => {
        setDeptReqLoading(true);
        try {
            const r = await wardInventoryAPI.getPharmacyRequests();
            setDeptRequests(r.data || []);
        } catch { } finally { setDeptReqLoading(false); }
    }, []);

    const handleDeptIssue = async () => {
        if (!deptIssueModal) return;
        try {
            await wardInventoryAPI.issueRequest(deptIssueModal.id, { qty_issued: deptIssueQty || deptIssueModal.qty_requested });
            toast.success('Issued!');
            setDeptIssueModal(null);
            loadDeptRequests();
        } catch { toast.error('Issue failed'); }
    };

    const handleDeptReject = async (id) => {
        const reason = window.prompt('Rejection reason:');
        if (reason === null) return;
        try {
            await wardInventoryAPI.rejectRequest(id, { rejection_reason: reason });
            toast.success('Rejected');
            loadDeptRequests();
        } catch { toast.error('Reject failed'); }
    };

    const loadWardData = useCallback(async () => {
        setWardReqLoading(true);
        try {
            const [wr, cons, ret, cnt] = await Promise.all([
                pharmacyWardAPI.getWardRequests(),
                pharmacyWardAPI.getConsumables(),
                pharmacyWardAPI.getReturns(),
                pharmacyWardAPI.getPendingCount(),
            ]);
            setWardRequests(wr.data || []);
            setConsumables(cons.data || []);
            setWardReturns(ret.data || []);
            setPendingCount(cnt.data || { ward_requests: 0, consumables: 0, returns: 0 });
        } catch { toast.error('Failed to load ward requests'); }
        finally { setWardReqLoading(false); }
    }, []);

    const handleIssue = async () => {
        if (!issueQty || issueQty < 1) { toast.error('Enter valid qty'); return; }
        try {
            await pharmacyWardAPI.issueRequest(issueModal.id, { issued_qty: parseInt(issueQty), drug_id: issueModal.drug_id });
            toast.success('Issued successfully');
            setIssueModal(null);
            setIssueQty('');
            loadWardData();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const handleReject = async (id) => {
        const reason = window.prompt('Rejection reason (optional):');
        if (reason === null) return;
        try {
            await pharmacyWardAPI.rejectRequest(id, { reason });
            toast.success('Request rejected');
            loadWardData();
        } catch { toast.error('Failed'); }
    };

    const handleAcceptReturn = async (id) => {
        if (!window.confirm('Accept this return and restore stock?')) return;
        try {
            await pharmacyWardAPI.acceptReturn(id);
            toast.success('Return accepted, stock restored');
            loadWardData();
        } catch { toast.error('Failed'); }
    };

    useEffect(() => { loadDrugs(); }, [loadDrugs]);
    useEffect(() => { loadLowStock(); }, []);
    useEffect(() => { if (tab === 'inventory') loadInventory(); }, [tab, loadInventory]);
    useEffect(() => { if (tab === 'ward') loadWardData(); }, [tab, loadWardData]);
    useEffect(() => { if (tab === 'dept') loadDeptRequests(); }, [tab, loadDeptRequests]);
    useEffect(() => { pharmacyWardAPI.getPendingCount().then(r => setPendingCount(r.data || {})).catch(() => {}); }, []);

    const handleAddDrug = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await pharmacyAPI.addDrug(drugForm);
            toast.success('Drug added');
            setShowAddDrug(false);
            setDrugForm({ name: '', generic_name: '', category: '', form: 'Tablet', strength: '', unit: 'Tablet', reorder_level: 10 });
            loadDrugs();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setLoading(false); }
    };

    const handleAddStock = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await pharmacyAPI.addStock({ drug_id: showAddStock.id, ...stockForm });
            toast.success('Stock added');
            setShowAddStock(null);
            setStockForm({ quantity: '', batch_number: '', expiry_date: '', supplier: '', purchase_price: '', sale_price: '' });
            loadDrugs();
            if (tab === 'inventory') loadInventory();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setLoading(false); }
    };

    const stockColor = (qty, reorder) => {
        if (qty === 0) return '#dc2626';
        if (qty <= reorder) return '#d97706';
        return '#16a34a';
    };

    const totalStockValue = inventory.reduce((s, i) => s + (parseFloat(i.purchase_price) || 0) * (parseInt(i.quantity) || 0), 0);

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>💊 Pharmacy</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Drug master, inventory & dispensing</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => navigate('/pharmacy/dispense')}
                        style={{ padding: '9px 18px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        💊 Dispense
                    </button>
                    <button onClick={() => navigate('/pharmacy/history')}
                        style={{ padding: '9px 18px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        📋 History
                    </button>
                    <button onClick={() => setShowAddDrug(true)}
                        style={{ padding: '9px 18px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        + Add Drug
                    </button>
                </div>
            </div>

            {/* Low stock alert */}
            {lowStock.length > 0 && (
                <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>⚠️</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                        {lowStock.length} drug(s) low/out of stock — <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setTab('lowstock')}>View</span>
                    </span>
                </div>
            )}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                {[['drugs', '💊 Drug Master'], ['inventory', '📦 Inventory'], ['lowstock', '⚠️ Low Stock']].map(([key, label]) => (
                    <button key={key} onClick={() => setTab(key)}
                        style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: tab === key ? '#1e3a5f' : '#f1f5f9', color: tab === key ? 'white' : '#374151' }}>
                        {label}
                    </button>
                ))}
                <button onClick={() => setTab('reports')}
                    style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        background: tab === 'reports' ? '#059669' : '#f1f5f9', color: tab === 'reports' ? 'white' : '#374151' }}>
                    📋 Medicine Report
                </button>
                <button onClick={() => setTab('ward')}
                    style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        background: tab === 'ward' ? '#7c3aed' : '#f1f5f9', color: tab === 'ward' ? 'white' : '#374151', position: 'relative' }}>
                    🏥 Ward Requests
                    {(pendingCount.ward_requests > 0 || pendingCount.consumables > 0 || pendingCount.returns > 0) && (
                        <span style={{ position: 'absolute', top: -4, right: -4, background: '#dc2626', color: 'white', borderRadius: '50%', fontSize: 10, fontWeight: 700, width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {pendingCount.ward_requests + pendingCount.consumables + pendingCount.returns}
                        </span>
                    )}
                </button>
                <button onClick={() => setTab('dept')}
                    style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        background: tab === 'dept' ? '#0891b2' : '#f1f5f9', color: tab === 'dept' ? 'white' : '#374151', position: 'relative' }}>
                    🏢 Dept. Requests
                    {deptRequests.filter(r => r.status === 'Pending').length > 0 && (
                        <span style={{ position: 'absolute', top: -4, right: -4, background: '#dc2626', color: 'white', borderRadius: '50%', fontSize: 10, fontWeight: 700, width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {deptRequests.filter(r => r.status === 'Pending').length}
                        </span>
                    )}
                </button>
            </div>

            {/* Drug Master Tab */}
            {tab === 'drugs' && (
                <>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search drug name or generic..."
                            style={{ flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
                        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
                            style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: 'white' }}>
                            <option value="">All Categories</option>
                            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Drug Name', 'Generic', 'Category', 'Form', 'Strength', 'Stock', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {drugs.length === 0 ? (
                                    <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No drugs found. Run seedMedicines.js to load Pakistan medicines.</td></tr>
                                ) : drugs.map(d => (
                                    <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600 }}>{d.name}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{d.generic_name || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>
                                            <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{d.category || '—'}</span>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{d.form || '—'}</td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{d.strength || '—'}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ fontWeight: 700, fontSize: 13, color: stockColor(parseInt(d.stock_qty), d.reorder_level) }}>
                                                {d.stock_qty} {d.unit}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <button onClick={() => { setShowAddStock(d); setStockForm({ quantity: '', batch_number: '', expiry_date: '', supplier: '', purchase_price: '', sale_price: '' }); }}
                                                style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                + Stock
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Inventory Tab */}
            {tab === 'inventory' && (
                <>
                    {/* Summary cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 18 }}>
                        {[
                            { label: 'Total Batches', value: inventory.length, color: '#1e3a5f', icon: '📦' },
                            { label: 'Total Items', value: inventory.reduce((s, i) => s + parseInt(i.quantity || 0), 0).toLocaleString(), color: '#059669', icon: '💊' },
                            { label: 'Stock Value', value: `Rs. ${totalStockValue.toLocaleString('en-PK', { minimumFractionDigits: 0 })}`, color: '#0369a1', icon: '💰' },
                            { label: 'Expiring Soon', value: inventory.filter(i => { if (!i.expiry_date) return false; const d = Math.ceil((new Date(i.expiry_date) - new Date()) / 86400000); return d >= 0 && d <= 90; }).length, color: '#d97706', icon: '⏰' },
                        ].map(c => (
                            <div key={c.label} style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: '14px 18px' }}>
                                <div style={{ fontSize: 22, marginBottom: 4 }}>{c.icon}</div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
                                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{c.label}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                        <input value={invSearch} onChange={e => setInvSearch(e.target.value)} placeholder="Search drug name, batch, supplier..."
                            style={{ flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
                    </div>

                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['Drug Name', 'Generic', 'Category', 'Batch No.', 'Qty', 'Expiry', 'Supplier', 'Purchase Price', 'Sale Price', 'Action'].map(h => (
                                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {invLoading ? (
                                    <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
                                ) : inventory.length === 0 ? (
                                    <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No stock records. Add stock from Drug Master tab.</td></tr>
                                ) : inventory.map(row => {
                                    const exp = expiryStatus(row.expiry_date);
                                    return (
                                        <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.drug_name}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{row.generic_name || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12 }}>
                                                <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{row.category || '—'}</span>
                                            </td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#475569', fontFamily: 'monospace' }}>{row.batch_number || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontWeight: 700, fontSize: 13, color: parseInt(row.quantity) === 0 ? '#dc2626' : '#16a34a' }}>
                                                {row.quantity} {row.unit}
                                            </td>
                                            <td style={{ padding: '9px 12px' }}>
                                                {exp ? (
                                                    <span style={{ background: exp.bg, color: exp.color, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{exp.label}</span>
                                                ) : '—'}
                                            </td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{row.supplier || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#475569' }}>{row.purchase_price ? `Rs. ${parseFloat(row.purchase_price).toFixed(2)}` : '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#059669', fontWeight: 600 }}>{row.sale_price ? `Rs. ${parseFloat(row.sale_price).toFixed(2)}` : '—'}</td>
                                            <td style={{ padding: '9px 12px' }}>
                                                <button onClick={() => { setShowAddStock({ id: row.drug_id, name: row.drug_name, strength: row.strength }); setStockForm({ quantity: '', batch_number: '', expiry_date: '', supplier: row.supplier || '', purchase_price: row.purchase_price || '', sale_price: row.sale_price || '' }); }}
                                                    style={{ padding: '4px 8px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                    + Restock
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Low Stock Tab */}
            {tab === 'lowstock' && (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#fef3c7' }}>
                                {['Drug Name', 'Generic', 'Category', 'Form', 'Current Stock', 'Reorder Level', 'Status', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #f59e0b' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {lowStock.length === 0 ? (
                                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>All stocks are sufficient ✅</td></tr>
                            ) : lowStock.map(d => (
                                <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600 }}>{d.name}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{d.generic_name || '—'}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{d.category || '—'}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{d.form || '—'}</td>
                                    <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: 13, color: parseInt(d.stock_qty) === 0 ? '#dc2626' : '#d97706' }}>{d.stock_qty} {d.unit}</td>
                                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b' }}>{d.reorder_level}</td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <span style={{ background: parseInt(d.stock_qty) === 0 ? '#fef2f2' : '#fffbeb', color: parseInt(d.stock_qty) === 0 ? '#dc2626' : '#d97706', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                                            {parseInt(d.stock_qty) === 0 ? 'OUT OF STOCK' : 'LOW STOCK'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <button onClick={() => { setShowAddStock(d); setStockForm({ quantity: '', batch_number: '', expiry_date: '', supplier: '', purchase_price: '', sale_price: '' }); setTab('drugs'); }}
                                            style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                            + Add Stock
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Medicine Report Tab */}
            {tab === 'reports' && <PharmacyWardReport />}

            {/* Dept. Requests Tab */}
            {tab === 'dept' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0891b2' }}>Departmental Pharmacy Requests</h3>
                        <button onClick={loadDeptRequests} style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Refresh</button>
                    </div>
                    {deptReqLoading ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div> : (
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f0f9ff' }}>
                                        {['Date', 'Ward/Dept', 'Item', 'Qty Req.', 'Unit', 'Notes', 'Requested By', 'Status', 'Actions'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#0891b2', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {deptRequests.length === 0 ? (
                                        <tr><td colSpan={9} style={{ padding: 30, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No departmental requests found</td></tr>
                                    ) : deptRequests.map(r => (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', background: r.status === 'Pending' ? '#fafeff' : '#fff' }}>
                                            <td style={{ padding: '9px 12px', fontSize: 12 }}>{new Date(r.created_at).toLocaleDateString('en-PK')}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600 }}>{r.ward_name || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 13 }}>{r.item_name}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 13 }}>{r.qty_requested}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{r.unit || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 12 }}>{r.requested_by_name || '—'}</td>
                                            <td style={{ padding: '9px 12px' }}>
                                                <span style={{ background: r.status === 'Pending' ? '#fef3c7' : r.status === 'Issued' ? '#d1fae5' : '#fee2e2', color: r.status === 'Pending' ? '#92400e' : r.status === 'Issued' ? '#065f46' : '#dc2626', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '9px 12px' }}>
                                                {r.status === 'Pending' && (
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button onClick={() => { setDeptIssueModal(r); setDeptIssueQty(r.qty_requested); }}
                                                            style={{ padding: '4px 10px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                            Issue
                                                        </button>
                                                        <button onClick={() => handleDeptReject(r.id)}
                                                            style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                                                            Reject
                                                        </button>
                                                    </div>
                                                )}
                                                {r.status !== 'Pending' && <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Issue Modal */}
                    {deptIssueModal && (
                        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                            <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 360 }}>
                                <h3 style={{ fontWeight: 700, marginBottom: 12 }}>Issue Item</h3>
                                <p style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}><strong>{deptIssueModal.item_name}</strong> — Ward: {deptIssueModal.ward_name}</p>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Qty to Issue</label>
                                <input type="number" min="1" max={deptIssueModal.qty_requested} value={deptIssueQty} onChange={e => setDeptIssueQty(e.target.value)}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, marginBottom: 16, boxSizing: 'border-box' }} />
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={handleDeptIssue} style={{ flex: 1, padding: 10, background: '#0891b2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>Confirm Issue</button>
                                    <button onClick={() => setDeptIssueModal(null)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Ward Requests Tab */}
            {tab === 'ward' && (
                <div>
                    {wardReqLoading ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div> : (
                        <>
                            {/* Medicine Requests */}
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>
                                Medicine Requests
                                {pendingCount.ward_requests > 0 && <span style={{ marginLeft: 8, background: '#dc2626', color: 'white', borderRadius: 20, padding: '1px 8px', fontSize: 11 }}>{pendingCount.ward_requests} Pending</span>}
                            </h3>
                            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 20 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f3ff' }}>
                                            {['Patient', 'MR#', 'Ward', 'Medicine', 'Qty', 'Status', 'Actions'].map(h => (
                                                <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #ddd6fe' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {wardRequests.length === 0 ? (
                                            <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No ward requests</td></tr>
                                        ) : wardRequests.map(r => (
                                            <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600 }}>{r.patient_name || '—'}</td>
                                                <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{r.mrn || '—'}</td>
                                                <td style={{ padding: '9px 12px', fontSize: 12 }}>{r.ward_name || '—'}</td>
                                                <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600 }}>{r.medicine_name}</td>
                                                <td style={{ padding: '9px 12px', fontSize: 13 }}>{r.requested_qty}</td>
                                                <td style={{ padding: '9px 12px' }}>
                                                    <span style={{ borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                                                        background: r.status === 'Pending' ? '#fef3c7' : r.status === 'Issued' ? '#f0fdf4' : r.status === 'Rejected' ? '#fef2f2' : '#f1f5f9',
                                                        color: r.status === 'Pending' ? '#d97706' : r.status === 'Issued' ? '#16a34a' : r.status === 'Rejected' ? '#dc2626' : '#374151' }}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '9px 12px' }}>
                                                    {r.status === 'Pending' && (
                                                        <div style={{ display: 'flex', gap: 6 }}>
                                                            <button type="button" onClick={() => { setIssueModal(r); setIssueQty(String(r.requested_qty)); }}
                                                                style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                                Issue
                                                            </button>
                                                            <button type="button" onClick={() => handleReject(r.id)}
                                                                style={{ padding: '4px 10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                                Reject
                                                            </button>
                                                        </div>
                                                    )}
                                                    {r.status !== 'Pending' && <span style={{ fontSize: 11, color: '#64748b' }}>{r.issued_qty ? `Issued: ${r.issued_qty}` : '—'}</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Returns */}
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#7c3aed', marginBottom: 8 }}>
                                Returns
                                {pendingCount.returns > 0 && <span style={{ marginLeft: 8, background: '#dc2626', color: 'white', borderRadius: 20, padding: '1px 8px', fontSize: 11 }}>{pendingCount.returns} Pending</span>}
                            </h3>
                            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 20 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f3ff' }}>
                                            {['Ward', 'Medicine', 'Qty', 'Reason', 'Status', 'Action'].map(h => (
                                                <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #ddd6fe' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {wardReturns.length === 0 ? (
                                            <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#64748b', fontSize: 13 }}>No returns</td></tr>
                                        ) : wardReturns.map(r => (
                                            <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '9px 12px', fontSize: 12 }}>{r.ward_name || '—'}</td>
                                                <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600 }}>{r.medicine_name}</td>
                                                <td style={{ padding: '9px 12px', fontSize: 13 }}>{r.returned_qty}</td>
                                                <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{r.reason || '—'}</td>
                                                <td style={{ padding: '9px 12px' }}>
                                                    <span style={{ borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
                                                        background: r.status === 'Pending' ? '#fef3c7' : '#f0fdf4',
                                                        color: r.status === 'Pending' ? '#d97706' : '#16a34a' }}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '9px 12px' }}>
                                                    {r.status === 'Pending' && (
                                                        <button type="button" onClick={() => handleAcceptReturn(r.id)}
                                                            style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                            Accept
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Issue Modal */}
            {issueModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 360 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e3a5f', marginBottom: 16 }}>Issue Medicine</h3>
                        <p style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
                            <strong>{issueModal.medicine_name}</strong> — {issueModal.patient_name} ({issueModal.ward_name})
                        </p>
                        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Requested: {issueModal.requested_qty}</p>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Issue Qty *</label>
                        <input type="number" min="1" value={issueQty} onChange={e => setIssueQty(e.target.value)}
                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 16 }} />
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button type="button" onClick={handleIssue}
                                style={{ flex: 1, padding: '10px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                Confirm Issue
                            </button>
                            <button type="button" onClick={() => { setIssueModal(null); setIssueQty(''); }}
                                style={{ flex: 1, padding: '10px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Drug Modal */}
            {showAddDrug && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 520, maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>Add New Drug</h3>
                            <button onClick={() => setShowAddDrug(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>
                        <form onSubmit={handleAddDrug}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Brand Name *</label>
                                    <input required style={inputStyle} value={drugForm.name} onChange={e => setDrugForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Panadol 500mg" />
                                </div>
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Generic Name</label>
                                    <input style={inputStyle} value={drugForm.generic_name} onChange={e => setDrugForm(f => ({ ...f, generic_name: e.target.value }))} placeholder="e.g. Paracetamol" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Category</label>
                                    <select style={selectStyle} value={drugForm.category} onChange={e => setDrugForm(f => ({ ...f, category: e.target.value }))}>
                                        <option value="">Select</option>
                                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Form</label>
                                    <select style={selectStyle} value={drugForm.form} onChange={e => setDrugForm(f => ({ ...f, form: e.target.value }))}>
                                        {FORMS.map(f => <option key={f}>{f}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Strength</label>
                                    <input style={inputStyle} value={drugForm.strength} onChange={e => setDrugForm(f => ({ ...f, strength: e.target.value }))} placeholder="e.g. 500mg" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Unit</label>
                                    <input style={inputStyle} value={drugForm.unit} onChange={e => setDrugForm(f => ({ ...f, unit: e.target.value }))} placeholder="Tablet / ml / vial" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Reorder Level</label>
                                    <input type="number" style={inputStyle} value={drugForm.reorder_level} onChange={e => setDrugForm(f => ({ ...f, reorder_level: e.target.value }))} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button type="submit" disabled={loading}
                                    style={{ flex: 1, padding: '10px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {loading ? 'Saving...' : 'Add Drug'}
                                </button>
                                <button type="button" onClick={() => setShowAddDrug(false)}
                                    style={{ flex: 1, padding: '10px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Stock Modal */}
            {showAddStock && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 460 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>Add Stock</h3>
                                <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>{showAddStock.name}</p>
                            </div>
                            <button onClick={() => setShowAddStock(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>
                        <form onSubmit={handleAddStock}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Quantity *</label>
                                    <input required type="number" style={inputStyle} value={stockForm.quantity} onChange={e => setStockForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Enter quantity" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Batch Number</label>
                                    <input style={inputStyle} value={stockForm.batch_number} onChange={e => setStockForm(f => ({ ...f, batch_number: e.target.value }))} placeholder="e.g. B2024-001" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Expiry Date</label>
                                    <input type="date" style={inputStyle} value={stockForm.expiry_date} onChange={e => setStockForm(f => ({ ...f, expiry_date: e.target.value }))} />
                                </div>
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Supplier</label>
                                    <input style={inputStyle} value={stockForm.supplier} onChange={e => setStockForm(f => ({ ...f, supplier: e.target.value }))} placeholder="Supplier / company name" />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Purchase Price (per unit)</label>
                                    <input type="number" step="0.01" style={inputStyle} value={stockForm.purchase_price} onChange={e => setStockForm(f => ({ ...f, purchase_price: e.target.value }))} placeholder="Rs." />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Sale Price (per unit)</label>
                                    <input type="number" step="0.01" style={inputStyle} value={stockForm.sale_price} onChange={e => setStockForm(f => ({ ...f, sale_price: e.target.value }))} placeholder="Rs." />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                                <button type="submit" disabled={loading}
                                    style={{ flex: 1, padding: '10px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    {loading ? 'Saving...' : 'Add Stock'}
                                </button>
                                <button type="button" onClick={() => setShowAddStock(null)}
                                    style={{ flex: 1, padding: '10px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
